import {
  Component,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '@victory-lib-ui/shared/auth';
import { resolveUserGivenName } from '@victory-lib-ui/shared/models';
import { DashboardFacadeService } from '../../../dashboard/dashboard-facade.service';
import { LayoutService } from '../../services/layout.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterModule, FormsModule, TranslocoDirective],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  private readonly authService = inject(AuthService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly layoutService = inject(LayoutService);
  private readonly dashboardFacade = inject(DashboardFacadeService);

  private readonly currentUserSignal = toSignal(this.authService.currentUser$, {
    initialValue: this.authService.currentUser,
  });
  readonly isAuthenticated = computed(() => !!this.currentUserSignal());
  readonly firstName = computed(() => {
    const user = this.currentUserSignal();
    return user ? resolveUserGivenName(user) : 'Guest';
  });
  readonly desktopSidebarCollapsed = this.layoutService.desktopSidebarCollapsed;
  readonly userAvatarSrc = computed(() =>
    this.authService.getResolvedAvatarUrl(this.currentUserSignal())
  );
  readonly streakCount = toSignal(this.dashboardFacade.readingStreakCount$, {
    initialValue:
      this.dashboardFacade.initialState.viewModel.readingStreak.current_days,
  });

  searchQuery = signal('');
  notificationCount = signal(3);
  isDarkMode = signal(false);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      const stored = localStorage.getItem('darkMode');
      if (stored === 'true') {
        this.isDarkMode.set(true);
        document.body.classList.add('dark-mode');
      }
    }
  }

  toggleDesktopSidebar(): void {
    this.layoutService.toggleDesktopSidebar();
  }

  toggleMobileSidebar(): void {
    this.layoutService.toggleSidebar();
  }

  submitSearch(): void {
    const query = this.searchQuery().trim();
    if (!query) {
      return;
    }

    this.router.navigate(['/search'], {
      queryParams: {
        query,
        version: 'KJV',
        type: 'text',
      },
    });
  }

  toggleDarkMode(): void {
    this.isDarkMode.update((v) => !v);
    if (isPlatformBrowser(this.platformId)) {
      document.body.classList.toggle('dark-mode', this.isDarkMode());
      localStorage.setItem('darkMode', String(this.isDarkMode()));
    }
  }

  logout(): void {
    this.authService.logout().subscribe();
  }
}
